import {
  feedbackAdminUpdateSchema,
  feedbackCreateResponseSchema,
  feedbackListResponseSchema,
  feedbackReportDetailSchema,
  feedbackSupportPromptSchema,
  type FeedbackAdminUpdate,
  type FeedbackCreatePayload,
  type FeedbackCreateResponse,
  type FeedbackListResponse,
  type FeedbackReportDetail,
} from '@erp/shared';
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import { apiFetch, apiUpload, type ApiError } from '@/lib/api-client';

export interface FeedbackFilters {
  readonly page: number;
  readonly q: string;
  readonly type: string;
  readonly status: string;
  readonly priority: string;
}

export const feedbackKeys = {
  all: ['admin', 'feedback'] as const,
  list: (filters: FeedbackFilters) => ['admin', 'feedback', filters] as const,
  detail: (id: string) => ['admin', 'feedback', id] as const,
};

export function useSubmitFeedback(): UseMutationResult<
  FeedbackCreateResponse,
  ApiError,
  { payload: FeedbackCreatePayload; screenshot: Blob | null; idempotencyKey: string }
> {
  return useMutation({
    mutationFn: ({ payload, screenshot, idempotencyKey }) => {
      const formData = new FormData();
      formData.set('payload', JSON.stringify(payload));
      if (screenshot !== null) formData.set('screenshot', screenshot, 'feedback-screenshot.png');
      return apiUpload('/api/v1/feedback', feedbackCreateResponseSchema, {
        formData,
        headers: { 'Idempotency-Key': idempotencyKey },
      });
    },
  });
}

function listQuery(filters: FeedbackFilters): string {
  const query = new URLSearchParams({
    page: String(filters.page),
    pageSize: '25',
  });
  if (filters.q.trim().length > 0) query.set('q', filters.q.trim());
  if (filters.type.length > 0) query.set('type', filters.type);
  if (filters.status.length > 0) query.set('status', filters.status);
  if (filters.priority.length > 0) query.set('priority', filters.priority);
  return query.toString();
}

export function useFeedbackReports(
  filters: FeedbackFilters,
): UseQueryResult<FeedbackListResponse, ApiError> {
  return useQuery({
    queryKey: feedbackKeys.list(filters),
    queryFn: () =>
      apiFetch(`/api/v1/admin/feedback?${listQuery(filters)}`, feedbackListResponseSchema),
  });
}

export function useFeedbackReport(
  id: string | null,
): UseQueryResult<FeedbackReportDetail, ApiError> {
  return useQuery({
    queryKey: feedbackKeys.detail(id ?? ''),
    enabled: id !== null,
    queryFn: () => apiFetch(`/api/v1/admin/feedback/${id ?? ''}`, feedbackReportDetailSchema),
  });
}

export function useUpdateFeedback(): UseMutationResult<
  FeedbackReportDetail,
  ApiError,
  { id: string; values: FeedbackAdminUpdate }
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, values }) => {
      const parsed = feedbackAdminUpdateSchema.parse(values);
      return apiFetch(`/api/v1/admin/feedback/${id}`, feedbackReportDetailSchema, {
        method: 'PATCH',
        body: parsed,
      });
    },
    onSuccess: (entry) => {
      queryClient.setQueryData(feedbackKeys.detail(entry.id), entry);
      void queryClient.invalidateQueries({ queryKey: feedbackKeys.all });
    },
  });
}

export function useCopyFeedbackPrompt(): UseMutationResult<
  { reference: string; prompt: string },
  ApiError,
  string
> {
  return useMutation({
    mutationFn: (id) =>
      apiFetch(`/api/v1/admin/feedback/${id}/support-prompt`, feedbackSupportPromptSchema, {
        method: 'POST',
      }),
  });
}
